'use client';

import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { type DeliverySize } from '@packages/gql/generated/shopping/graphql';
import { useLayerTracking } from '../Tracking/hooks/useLayerTracking/useLayerTracking';
import { ServiceLayer } from '../ServiceLayer/ServiceLayer.client';
import { TextWithDialog } from '../TextWithDialog/TextWithDialog.client';
import { taxHintMessages } from './messages';
import { getTaxHintMessage } from './getTaxHintMessage';

export type TaxHintProps = {
  /**
   * The used delivery type for a given product. Each delivery type will show its own delivery info message.
   * */
  deliverySize: DeliverySize;
};

/**
 * <p>
 * The <code>TaxHint</code> component displays a delivery info message.
 * </p>
 * */
export const TaxHint = ({ deliverySize }: TaxHintProps) => {
  const { formatMessage } = useIntl();
  const { trackViewLayer } = useLayerTracking();
  const {
    product: {
      taxHint: { hideAdditionalDeliveryInfo, modalContent },
    },
  } = useConfig();

  const dialogContent =
    deliverySize in modalContent ? (
      <ServiceLayer serviceLayerId={modalContent[deliverySize].link} />
    ) : (
      <>{formatMessage(taxHintMessages.dialogContent)}</>
    );

  const title = formatMessage(getTaxHintMessage('TITLE', deliverySize));

  const handleDialogOpen = () => {
    trackViewLayer(`${title} (Liefertyp: ${deliverySize})`);
  };

  const messageValue = {
    link: (msg: ReactNode[]) =>
      !hideAdditionalDeliveryInfo && (
        <TextWithDialog
          message={msg.join('')}
          dialogTitle={title}
          dialogContent={dialogContent}
          onOpenDialogHandler={handleDialogOpen}
          className="text-color-text-darktransparent"
          displayTrackingProps={{ category: 'dv-service-layer-tax-hint' }}
          key={msg.join('')}
        />
      ),
  };

  return (
    /* must be a div because the inner text has a dialog rendered inside and a dialog is not valid in a p */
    <div className="text-size-body-xs text-color-text-darktransparent">
      {formatMessage(getTaxHintMessage('TEXT', deliverySize), messageValue)}
    </div>
  );
};
